import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { OperationalNavChrome } from '~features/new-dashboard/ui/operational-nav-chrome';
import { PortalBanner } from '~features/portal-banner';

import { getCurrentStudio } from '~entities/studios/model/studio-store';
import { useGetCurrentUser } from '~entities/users/api/get-current-user';
import { usePreventIframeScrollChain } from '~shared/hooks/use-prevent-iframe-scroll-chain';
import { isEmbeddedInCrmIframe } from '~shared/utils/is-embedded-in-crm-iframe';
import { cn } from '~shared/shadcn/lib/utils';
import {
  pickLoginLocationId,
  readIdFromSearch,
  redirectForUnauthenticatedPath,
} from '~shared/utils/studio-login-redirect';

export const Route = createFileRoute('/_auth')({
  component: RouteComponent,
});

function PendingComponent() {
  return (
    <div className='flex h-screen items-center justify-center'>
      <Loader2 className='size-10 animate-spin' />
    </div>
  );
}

function RouteComponent() {
  const {
    data: currentUser,
    isLoading: isCurrentUserLoading,
    isError: isCurrentUserError,
  } = useGetCurrentUser();
  const studio = getCurrentStudio();
  const navigate = useNavigate();

  // GHL iframe only: stop wheel overscroll from sliding the parent CRM page
  // and clipping the sticky navbar / logo in half at the document edges.
  usePreventIframeScrollChain();

  // Height of the sticky chrome (nav + banners) stacked above the page.
  // Exposed as `--chrome-h` so `min-h-screen` pages fill the remaining
  // space instead of overflowing the GHL iframe (see index.css).
  //
  // Measure banner + nav as siblings — do NOT wrap the sticky TopNavBar in a
  // short parent (that broke sticky after the ENR-4041 iframe fit fix).
  const appRootRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const [chromeH, setChromeH] = useState(0);

  useEffect(() => {
    const root = appRootRef.current;
    if (!root) {
      return;
    }

    const measure = () => {
      const bannerH = bannerRef.current?.offsetHeight ?? 0;
      const nav = root.querySelector('[data-enrollio-top-nav]');
      const demo = root.querySelector('[data-enrollio-demo-banner]');
      const navH = nav instanceof HTMLElement ? nav.offsetHeight : 0;
      const demoH = demo instanceof HTMLElement ? demo.offsetHeight : 0;
      setChromeH(bannerH + navH + demoH);
    };

    const resizeObserver = new ResizeObserver(measure);
    const watch = () => {
      resizeObserver.disconnect();
      if (bannerRef.current) {
        resizeObserver.observe(bannerRef.current);
      }
      for (const sel of [
        '[data-enrollio-top-nav]',
        '[data-enrollio-demo-banner]',
      ]) {
        const el = root.querySelector(sel);
        if (el) {
          resizeObserver.observe(el);
        }
      }
      measure();
    };

    watch();
    // Nav/banner mount after auth resolves; re-attach observers when they appear.
    const mutationObserver = new MutationObserver(watch);
    mutationObserver.observe(root, { childList: true, subtree: true });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [currentUser]);

  if (isCurrentUserLoading) {
    return <PendingComponent />;
  }

  if (isCurrentUserError || !currentUser) {
    // ENR-4044: in the CRM iframe, re-run SSO instead of stranding on /login.
    if (isEmbeddedInCrmIframe()) {
      void navigate({ to: '/exchange-user-context' });
    } else {
      void navigate({
        to: '/login',
        search: {
          id: pickLoginLocationId(
            readIdFromSearch(window.location.search),
            studio?.locationId,
          ),
          redirect: redirectForUnauthenticatedPath(window.location.pathname),
        },
      });
    }
  }

  // Skip password setup for CRM Agency Admins - they bypass the new login screen
  if (currentUser?.noPassword && !currentUser?.isAgencyAdmin) {
    void navigate({
      to: '/set-password',
    });
  }

  const embeddedInCrm = isEmbeddedInCrmIframe();

  return (
    <div
      ref={appRootRef}
      className={cn(
        'enrollio-app-root w-full min-w-0',
        // ENR-4098: when framed, fill the locked iframe box and scroll here.
        // Standalone keeps min-h-dvh so the document remains the scrollport.
        embeddedInCrm ? 'h-full' : 'min-h-dvh',
      )}
      style={{ '--chrome-h': `${chromeH}px` } as CSSProperties}
    >
      {/* Banner + nav are siblings (pre-4041 sticky layout). Nav keeps its own sticky. */}
      <div ref={bannerRef}>
        {currentUser ? <PortalBanner /> : null}
      </div>
      {currentUser ? <OperationalNavChrome /> : null}
      <Outlet />
    </div>
  );
}
